/**
 * Profile Menu Item Row Component
 *
 * @module modules/user/presentation/shared/components/ProfileMenuItemRow
 *
 * Individual menu item for the profile dropdown.
 * Supports collapsed/mobile mode showing only icons.
 */

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { memo } from 'react';

import { ThemeToggleSwitch } from './ThemeToggleSwitch';

import type { ProfileMenuItem } from '../types';

interface ProfileMenuItemRowProps {
  /** Menu item data */
  item: ProfileMenuItem;
  /** Whether in collapsed/mobile mode (icons only) */
  collapsed?: boolean;
  /** Whether this is the last item (no border) */
  isLast?: boolean;
  /** Whether dark mode is active (for theme toggle) */
  isDark?: boolean;
  /** Whether logout is in progress */
  isLoggingOut?: boolean;
  /** Click handler */
  onClick: (e: React.MouseEvent) => void;
}

/**
 * Profile Menu Item Row Component
 *
 * Renders a single menu item with support for:
 * - Normal and collapsed/mobile modes
 * - Theme toggle switch
 * - Loading state for logout
 *
 * @example
 * ```tsx
 * <ProfileMenuItemRow
 *   item={{ label: 'Settings', icon: <SettingsIcon /> }}
 *   collapsed={isMobile}
 *   onClick={handleClick}
 * />
 * ```
 */
export const ProfileMenuItemRow = memo(function ProfileMenuItemRow({
  item,
  collapsed = false,
  isLast = false,
  isDark = false,
  isLoggingOut = false,
  onClick,
}: ProfileMenuItemRowProps) {
  const muiTheme = useMuiTheme();

  const isLoading = isLoggingOut && item.isLogout;
  const showLoadingState = isLoading;

  // In collapsed mode, show only icon with tooltip
  if (collapsed) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        <Box
          onClick={onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1.25,
            cursor: showLoadingState ? 'wait' : 'pointer',
            borderBottom: isLast ? 0 : 1,
            borderColor: 'divider',
            color: item.isLogout ? 'error.main' : 'text.secondary',
            opacity: showLoadingState ? 0.7 : 1,
            transition: muiTheme.transitions.create(['background-color', 'color']),
            '&:hover': {
              bgcolor: item.isLogout
                ? alpha(muiTheme.palette.error.main, 0.08)
                : alpha(muiTheme.palette.background.default, 0.8),
              color: item.isLogout ? 'error.main' : 'text.primary',
            },
            '& .MuiSvgIcon-root': {
              fontSize: 20,
              color: 'inherit',
            },
          }}
        >
          {showLoadingState ? (
            <CircularProgress size={20} color="error" />
          ) : item.isThemeToggle ? (
            <ThemeToggleSwitch isDark={isDark} />
          ) : (
            item.icon
          )}
        </Box>
      </Tooltip>
    );
  }

  // Normal expanded mode
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 1.25,
        cursor: showLoadingState ? 'wait' : 'pointer',
        borderBottom: isLast ? 0 : 1,
        borderColor: 'divider',
        color: item.isLogout ? 'error.main' : 'text.secondary',
        opacity: showLoadingState ? 0.7 : 1,
        transition: muiTheme.transitions.create(['background-color', 'color']),
        '&:hover': {
          bgcolor: item.isLogout
            ? alpha(muiTheme.palette.error.main, 0.08)
            : alpha(muiTheme.palette.background.default, 0.8),
          color: item.isLogout ? 'error.main' : 'text.primary',
        },
        '& .MuiSvgIcon-root': {
          fontSize: 18,
          color: 'inherit',
        },
      }}
    >
      {showLoadingState ? (
        <CircularProgress size={18} color="error" />
      ) : (
        item.icon
      )}
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          flex: 1,
        }}
      >
        {showLoadingState ? 'Logging out...' : item.label}
      </Typography>
      {/* Theme Toggle Switch */}
      {item.isThemeToggle && <ThemeToggleSwitch isDark={isDark} />}
    </Box>
  );
});

